import React from 'react';
import { Body7, Container } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { REVIEW_GROUP_STATUSES, REVIEW_SUBJECTS } from '@services/fmdc';
import { FilterGroup, GroupLabel, SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { FilterRow, FilterSelect } from './styles';
import { QUEUE_SUBJECTS, queueUrl } from './fmdcUrl';
import { formatCount, formatReviewReason } from './format';

// The queue's filter bar. Every control is URL-owned (`useUrlFilters(queueUrl)`), and the two
// selects are built from the RUN rather than from a constant: a reason that has no open rows is
// never offered (the feedView filterBarModel rule — a control that can only narrow to nothing is
// worse than no control), and the sheets are whatever this model has.

const SUBJECT_SEGMENTS = [
  { key: REVIEW_SUBJECTS.EXTRACTION, label: 'Extraction' },
  { key: REVIEW_SUBJECTS.UNMAPPED, label: 'Unmapped' },
  { key: QUEUE_SUBJECTS.BOTH, label: 'Both' },
];

const STATUS_SEGMENTS = [
  { key: REVIEW_GROUP_STATUSES.OPEN, label: 'Open' },
  { key: REVIEW_GROUP_STATUSES.ALL, label: 'All' },
];

// `sheet_inventory` is documented only as present on a run; the shape is read defensively — an
// array of sheet objects (name under `name`, `sheet` or `sheet_name`), an array of strings, or an
// object keyed by sheet name.
export const sheetNamesFrom = (inventory) => {
  if (!inventory) return [];
  if (Array.isArray(inventory)) {
    return inventory
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry === 'object') return entry.name || entry.sheet || entry.sheet_name || null;
        return null;
      })
      .filter(Boolean);
  }
  if (typeof inventory === 'object') return Object.keys(inventory);
  return [];
};

// `[{ reason, count }]` from `review.by_reason`, zero-count reasons dropped, largest first.
export const reasonOptionsFrom = (byReason) => {
  if (!byReason || typeof byReason !== 'object') return [];
  return Object.entries(byReason)
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .map(([reason, count]) => ({ reason, count: Number(count) }));
};

export const isQueueFiltering = (filters) =>
  filters.subject !== queueUrl.defaults.subject ||
  filters.status !== queueUrl.defaults.status ||
  !!filters.reason ||
  !!filters.sheet;

export const ReviewFilters = ({ filters, setFilters, run, total }) => {
  const reasons = reasonOptionsFrom(run?.review?.by_reason);
  const sheets = sheetNamesFrom(run?.sheet_inventory);
  const filtering = isQueueFiltering(filters);
  const openCount = run?.review?.open;

  // A reason or sheet the URL carries that the run does not know about stays selectable so the
  // control shows what the list is filtered by, rather than silently reading as "All".
  const reasonChoices =
    filters.reason && !reasons.some((r) => r.reason === filters.reason)
      ? [...reasons, { reason: filters.reason, count: null }]
      : reasons;
  const sheetChoices = filters.sheet && !sheets.includes(filters.sheet) ? [...sheets, filters.sheet] : sheets;

  const clearFilters = () =>
    setFilters({
      subject: queueUrl.defaults.subject,
      status: queueUrl.defaults.status,
      reason: '',
      sheet: '',
      offset: 0,
    });

  return (
    <>
      <FilterRow>
        <SegmentedControl
          label="Subject"
          options={SUBJECT_SEGMENTS}
          active={filters.subject}
          onSelect={(subject) => setFilters({ subject })}
        />
        <SegmentedControl
          label="Status"
          options={STATUS_SEGMENTS}
          active={filters.status}
          onSelect={(status) => setFilters({ status })}
        />
        <FilterGroup>
          <GroupLabel as="label" htmlFor="fmdc-review-reason">
            Reason
          </GroupLabel>
          <FilterSelect
            id="fmdc-review-reason"
            aria-label="Filter by reason"
            value={filters.reason}
            onChange={(e) => setFilters({ reason: e.target.value })}
          >
            <option value="">All reasons</option>
            {reasonChoices.map(({ reason, count }) => (
              <option key={reason} value={reason}>
                {count === null ? formatReviewReason(reason) : `${formatReviewReason(reason)} (${formatCount(count)})`}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
        <FilterGroup>
          <GroupLabel as="label" htmlFor="fmdc-review-sheet">
            Sheet
          </GroupLabel>
          <FilterSelect
            id="fmdc-review-sheet"
            aria-label="Filter by sheet"
            $width={160}
            value={filters.sheet}
            onChange={(e) => setFilters({ sheet: e.target.value })}
          >
            <option value="">All sheets</option>
            {sheetChoices.map((sheet) => (
              <option key={sheet} value={sheet}>
                {sheet}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
      </FilterRow>
      <Container top={12} row verticallyCenter gap={12}>
        <Body7 isLowContrast role="status">
          {openCount !== undefined && openCount !== null
            ? `${formatCount(openCount)} open · ${formatCount(total)} shown`
            : `${formatCount(total)} shown`}
        </Body7>
        {filtering && <TransparentFlatButton onClick={clearFilters}>Clear filters</TransparentFlatButton>}
      </Container>
    </>
  );
};
